import {UnitSystem} from "../api/Projects";
import {ImperialDistance} from "./ImperialDistance";
import {MetricDistance} from "./MetricDistance";

// formatSmallUnits shows a stored dimension in the small unit of the system (inches, mm)
// rather than feet+inches. Use it for values that are always smaller than a foot, e.g. glass
// thickness. The stored value may be in either system.
export function formatSmallUnits(value: string | undefined, sys: UnitSystem): string {
    if(!value) return "";

    const uu = value.indexOf("mm") !== -1 ?
        MetricDistance.parse(value).universalValue() :
        ImperialDistance.parse(value).universalValue();

    if(sys === "metric") {
        return MetricDistance.fromUniversal(uu).toString();
    }

    return ImperialDistance.fromUniversal(uu).toInchesString();
}

// feet and/or inches, e.g. 24, 24.5", 24 1/2", 1/2", 2', 2' 6 1/2", 2ft 6in
const imperialInches = `(?:\\d+(?:\\.\\d+)?(?:\\s+\\d+/\\d+)?|\\d+/\\d+)\\s*(?:"|in)?`;
const imperialFeet = `\\d+(?:\\.\\d+)?\\s*(?:'|ft)`;
const imperialFormat = new RegExp(`^(?:${imperialFeet}(?:\\s*${imperialInches})?|${imperialInches})$`, "i");

// normalizeSmallUnits parses a typed dimension in the project's unit system and returns it in the
// system's small unit (inches, mm). A bare number is read as that unit. Throws when the value is
// written in the other system's units, can't be read, or is zero (unless allowZero).
export function normalizeSmallUnits(value: string, sys: UnitSystem, opts?: {allowZero?: boolean}): string {
    // shop notation: 24-1/2 means 24 1/2
    const trimmed = value.trim().replace(/(\d)\s*-\s*(\d+\/\d+)/, "$1 $2");

    if(sys === "metric") {
        let mm: number;
        try {
            mm = MetricDistance.parse(trimmed).mm;
        } catch (e) {
            throw new Error(`Use metric units (e.g. 610 mm)`);
        }

        if(mm === 0 && opts?.allowZero) return new MetricDistance(0).toString();
        if(mm <= 0) throw new Error("Must be greater than 0");
        return new MetricDistance(mm).toString();
    }

    if(/[0-9.\s](mm|cm|m)$/i.test(trimmed)) {
        throw new Error(`Use imperial units (e.g. 24 1/2")`);
    }

    if(!imperialFormat.test(trimmed)) {
        throw new Error(`Invalid dimension (e.g. 24 1/2")`);
    }

    let dist: ImperialDistance;
    try {
        dist = ImperialDistance.parse(trimmed);
    } catch (e) {
        throw new Error(`Invalid dimension (e.g. 24 1/2")`);
    }

    if(dist.sixtyFourthsOfInch === 0 && opts?.allowZero) return `0"`;
    if(dist.sixtyFourthsOfInch <= 0) throw new Error("Must be greater than 0");
    return dist.toInchesString();
}
